import { fetchWithAuth } from '@/lib/api/core'
import { resolveMarkdownImageSrc } from '@/lib/resolve-signed-url'
import { waitForAccessToken } from '@/lib/wait-for-access-token'

const blobCache = new Map<string, string>()
const blobRefCount = new Map<string, number>()
const inflight = new Map<string, Promise<string>>()

export function isAttachmentApiPath(src: string): boolean {
  return src.includes('/api/attachments/') || src.includes('/api/images/')
}

function retainBlobUrl(url: string): void {
  if (!url.startsWith('blob:')) return
  blobRefCount.set(url, (blobRefCount.get(url) || 0) + 1)
}

/** Decrement ref count; revoke blob URL only when no consumers remain. */
export function revokeAuthenticatedBlobUrl(url: string | null | undefined): void {
  if (!url?.startsWith('blob:')) return
  const count = (blobRefCount.get(url) || 0) - 1
  if (count <= 0) {
    blobRefCount.delete(url)
    URL.revokeObjectURL(url)
    for (const [key, cached] of blobCache.entries()) {
      if (cached === url) {
        blobCache.delete(key)
        break
      }
    }
  } else {
    blobRefCount.set(url, count)
  }
}

/**
 * Resolve markdown image src. Attachment API paths are fetched with Bearer auth
 * and returned as blob: URLs (browser <img> cannot send Authorization headers).
 */
export async function loadAuthenticatedImageSrc(rawSrc: string): Promise<string> {
  const resolved = resolveMarkdownImageSrc(rawSrc || '')
  if (!resolved) return ''

  if (!isAttachmentApiPath(resolved)) {
    return resolved
  }

  const cached = blobCache.get(resolved)
  if (cached) {
    retainBlobUrl(cached)
    return cached
  }

  let pending = inflight.get(resolved)
  if (!pending) {
    pending = (async () => {
      if (!(await waitForAccessToken())) {
        throw new Error('Authentication required')
      }
      const response = await fetchWithAuth(resolved)
      if (!response.ok) {
        throw new Error(`Attachment image failed (${response.status})`)
      }
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      blobCache.set(resolved, blobUrl)
      return blobUrl
    })().finally(() => {
      inflight.delete(resolved)
    })
    inflight.set(resolved, pending)
  }

  const blobUrl = await pending
  retainBlobUrl(blobUrl)
  return blobUrl
}
